// Per-tool descriptions. `intro` renders above the tool area (inside the calc body),
// `about` renders below the tool area (Calculator.net-style long-form article).
// Keep intros ≤ 3 short sentences. About sections are 3-5 short paragraphs.

export const DESCRIPTIONS = {
  // ————— FINANCIAL —————
  mortgage: {
    intro: 'Estimate the true cost of home ownership. Enter the loan amount, term, interest rate and taxes to see your monthly PITI payment.',
    about: [
      'A mortgage is a long-term loan used to finance the purchase of real estate. Each monthly payment is amortized into principal and interest so the balance is fully paid off by the end of the term. Property tax, homeowner insurance and — for high-LTV loans — private mortgage insurance (PMI) are typically escrowed with the payment (PITI).',
      'The payment formula is <b>M = P · r(1+r)ⁿ / ((1+r)ⁿ − 1)</b> where P is the principal, r is the monthly interest rate and n is the number of monthly payments. Because interest is front-loaded, early payments contribute mostly interest and later payments mostly principal.',
      'Making even one extra payment per year, adding to principal each month, or moving to a bi-weekly schedule can save tens of thousands of dollars over the life of a 30-year mortgage.'
    ]
  },
  loan: {
    intro: 'Compute the monthly payment and total interest of any amortized loan.',
    about: [
      'An amortized loan is repaid in equal periodic installments consisting of both principal and interest. Common examples include auto loans, student loans, home-equity loans and personal loans.',
      'Interest is charged only on the outstanding balance, so as the balance shrinks the interest portion of each payment falls and the principal portion rises. This is why paying extra toward principal early saves so much interest.',
      'Watch the <b>APR vs interest rate</b> distinction: APR includes fees while the nominal rate does not. To compare offers fairly, compare APRs.'
    ]
  },
  'auto-loan': {
    intro: 'Estimate monthly payment on a car loan, including sales tax, fees and trade-in.',
    about: [
      'Auto loans are secured by the vehicle and typically last 36-84 months. A larger down payment and shorter term dramatically reduce total interest paid.',
      'Cars depreciate the moment they leave the lot — putting little or nothing down often creates <b>negative equity</b> where you owe more than the car is worth. Consider gap insurance or a bigger down payment to avoid this.'
    ]
  },
  'fha-loan': {
    intro: 'FHA loans are government-backed mortgages with lower down-payment requirements — but with mandatory MIP.',
    about: [
      'The Federal Housing Administration insures FHA loans, allowing lenders to accept down payments as low as 3.5% with a credit score of 580+. Borrowers pay an <b>Upfront MIP</b> (typically 1.75% of the loan, financed) and an <b>Annual MIP</b> (0.15%–0.75% depending on LTV and term).',
      'MIP protects the lender, not the borrower. Unlike PMI on conventional loans, FHA MIP generally cannot be cancelled — it stays for the life of the loan when the down payment is below 10%.'
    ]
  },
  'va-mortgage': {
    intro: 'VA mortgages are available to eligible veterans, active-duty service members and surviving spouses. 0% down is common.',
    about: [
      'VA loans are guaranteed by the U.S. Department of Veterans Affairs. They allow <b>0% down</b>, do not require mortgage insurance, and often carry lower interest rates than conventional loans.',
      'A one-time <b>VA Funding Fee</b> (typically 1.25%–3.30% of the loan) is charged and can be rolled into the loan. Veterans with a service-connected disability are exempt from the funding fee.'
    ]
  },
  'personal-loan': {
    intro: 'Unsecured personal loans for debt consolidation, medical bills, home improvement or major purchases.',
    about: [
      'Personal loans are typically unsecured, meaning no collateral is required. Terms range 2-7 years with fixed rates. Lenders base pricing on credit score, income and debt-to-income ratio.',
      'Watch the <b>origination fee</b> (1%–8%) which is often deducted from proceeds — reducing cash-in-hand and effectively raising the APR. Always compare the effective APR, not just the interest rate.'
    ]
  },
  'business-loan': {
    intro: 'Commercial financing for equipment, working capital, real estate or expansion. Supports amortized, interest-only and balloon structures.',
    about: [
      'Business loans can be amortized (equal payments), interest-only (principal balloons at maturity), or partially amortized with a large balloon payment at a set date. Rates depend on the business\'s credit history, revenue and collateral.',
      'SBA-guaranteed loans offer longer terms and lower down payments but require extensive paperwork. Equipment financing uses the equipment itself as collateral.'
    ]
  },

  // ————— FITNESS —————
  bmi: {
    intro: 'Body Mass Index is a screening tool that estimates body fatness based on your height and weight.',
    about: [
      'BMI = weight (kg) / height (m)². For most adults, BMI < 18.5 is underweight, 18.5-24.9 healthy, 25-29.9 overweight, ≥ 30 obese. BMI does not distinguish between muscle and fat — very fit or muscular people may register as overweight.',
      'BMI is best used as a starting point, not a diagnosis. Combine with waist circumference and body-fat percentage for a fuller picture. For children and teens, use age- and sex-specific BMI percentiles.'
    ]
  },
  bmr: {
    intro: 'Your Basal Metabolic Rate is the number of calories your body burns at complete rest.',
    about: [
      'BMR represents the energy required to keep your organs functioning — heart beating, brain thinking, cells regenerating. It typically accounts for 60-75% of your total daily calorie burn.',
      'The Mifflin-St Jeor formula is the most accurate: <b>BMR = 10 · weight(kg) + 6.25 · height(cm) − 5 · age + s</b> where s is +5 for males and -161 for females. Multiply BMR by an activity factor (1.2 sedentary → 1.9 elite athlete) to estimate TDEE.'
    ]
  },
  calorie: {
    intro: 'Estimate your daily calorie needs based on age, sex, weight, height and activity level.',
    about: [
      'Total Daily Energy Expenditure (TDEE) is BMR multiplied by an activity factor. To lose weight, eat 300-500 kcal below TDEE; to gain muscle, eat 200-500 kcal above.',
      'Sustainable weight loss is 0.5-1 kg per week (2000-3500 kcal deficit / week). Faster loss tends to include muscle mass, not just fat.'
    ]
  },
  'body-fat': {
    intro: 'Estimate body fat percentage using the U.S. Navy tape-measurement method.',
    about: [
      'The Navy method uses neck and waist (and hip for women) circumferences to estimate body-fat percentage, with mean error of ~3-4% vs DEXA. Bioelectrical impedance scales are convenient but affected by hydration.',
      'Healthy body-fat ranges: men 10-20%, women 18-28%. Essential fat is 3-5% for men, 10-13% for women — going below is dangerous.'
    ]
  },

  // ————— MATH —————
  scientific: {
    intro: 'A full scientific calculator with trigonometry, logarithms, exponentials, memory and constants.',
    about: [
      'Trigonometric functions accept degrees or radians (toggle at the top). Inverse trig returns the principal value.',
      'Use parentheses to control operator precedence: <code>2 + 3 · 4 = 14</code> but <code>(2 + 3) · 4 = 20</code>. The scientific calculator follows PEMDAS.'
    ]
  },
  fraction: {
    intro: 'Add, subtract, multiply and divide fractions and mixed numbers — with step-by-step working.',
    about: [
      'Fractions represent parts of a whole. To add or subtract them, find a common denominator; to multiply, multiply numerators and denominators; to divide, multiply by the reciprocal.',
      'Always simplify at the end by dividing numerator and denominator by their greatest common divisor (GCD).'
    ]
  },
  percentage: {
    intro: 'Compute a percentage of a number, percent change, and reverse-percentage problems.',
    about: [
      '"X percent of Y" = X / 100 · Y. Percent change = (new − old) / old · 100%. Percent difference of two values = |a−b| / ((a+b)/2) · 100%.',
      'Be careful with sequential percent changes: a 20% loss then 20% gain returns 96%, not 100%.'
    ]
  },
  'standard-deviation': {
    intro: 'Compute mean, variance, standard deviation and coefficient of variation for a data set.',
    about: [
      'Standard deviation measures the spread of a distribution around its mean. Sample SD divides by (n−1) — Bessel\'s correction — to give an unbiased estimator; population SD divides by n.',
      'In a normal distribution, 68% of values lie within ±1 σ, 95% within ±2 σ, and 99.7% within ±3 σ of the mean.'
    ]
  },

  // ————— OTHER —————
  time: {
    intro: 'Add or subtract time, find the interval between two times, project a date + time forward or evaluate a full expression like 2h 30m + 45s.',
    about: [
      'The <b>Add / Subtract</b> tab treats hours, minutes and seconds as a single duration. The <b>Time Between</b> tab returns the elapsed interval and handles crossing midnight.',
      'The <b>Expression</b> tab accepts free-form input: use tokens like <code>2d 3h 45m 30s</code> combined with + / -. Long forms (<code>hours</code>, <code>minutes</code>) also work.'
    ]
  },
  date: {
    intro: 'Compute the difference between two dates or add / subtract days with optional holiday and business-day settings.',
    about: [
      'The difference is presented as years-months-days plus totals in days, weeks and <b>business days</b> (excluding weekends and — optionally — bank holidays for the selected country).',
      'When adding or subtracting days you can restrict to business days only (skip Saturday/Sunday) and additionally skip country-specific holidays.'
    ]
  },
  hours: {
    intro: 'Compute hours worked between two times (with a break) or between two full datetimes.',
    about: [
      'Enter start and end times to get total worked hours as HH:MM and decimal hours; subtract a lunch break in minutes. Times that cross midnight are handled correctly.',
      'The <b>Between Two Dates</b> tab returns total hours over a span of days including weeks and approximate months.'
    ]
  },
  concrete: {
    intro: 'Estimate concrete volume in cubic yards, cubic feet and 60/80-lb bag counts for the five most common pour shapes.',
    about: [
      'For accurate ordering, always order ~10% extra to account for spillage and formwork irregularities. Ready-mix concrete is sold by the cubic yard; bagged concrete is by cubic foot.',
      'Rule of thumb: one 60-lb bag ≈ 0.45 ft³, one 80-lb bag ≈ 0.6 ft³, one cubic yard ≈ 40 × 80-lb bags.'
    ]
  },
  roofing: {
    intro: 'Estimate roof area, roofing squares and bundles for a gable roof — plus pitch/angle conversion and material cost.',
    about: [
      'One <b>roofing square</b> equals 100 sq ft. Standard 3-tab shingles use ~3 bundles per square. Always add 10% waste for hips, valleys and starter courses.',
      'Roof pitch is expressed as rise per 12 inches of run. A 6/12 pitch is about 26.6°, 8/12 is 33.7°, and 12/12 is 45°.'
    ]
  },
  stair: {
    intro: 'Design a stair — number of risers, riser height, tread depth, stringer length and stair angle — with a 10-variation table.',
    about: [
      'International Residential Code (IRC) requires risers 4"-7¾" and treads ≥ 10". Ideal comfort is 7" rise / 11" tread.',
      'Use <b>2R + T ≈ 25"</b> as a comfort check: two-riser-heights plus one tread depth should equal ~25 inches. Anything outside 24-25" will feel steep or shallow.'
    ]
  },
  tile: {
    intro: 'Estimate tiles needed for a room, factoring in grout gap and waste, plus material cost.',
    about: [
      'Order 10-15% extra tile to allow for cuts, breakage and future repairs. For diagonal layouts or complex patterns, plan for 15-20% extra.',
      'Grout gap increases the effective footprint of each tile, so it must be added to tile dimensions when computing coverage.'
    ]
  },
  mulch: {
    intro: 'Compute cubic yards, cubic feet or bags of mulch needed for a garden bed.',
    about: [
      'Apply mulch 2-4" deep for weed suppression and moisture retention. Deeper than 4" can suffocate roots and encourage fungal growth.',
      'Bulk mulch is sold by the cubic yard (27 ft³) and is dramatically cheaper than bagged (0.5 - 3 ft³ each) once you need more than about 10 bags.'
    ]
  },
  gravel: {
    intro: 'Estimate cubic yards, tons and cost of gravel for driveways, paths and drainage projects.',
    about: [
      'Gravel density varies with the stone: pea gravel ≈ 1.28 t/yd³, crushed #57 ≈ 1.42 t/yd³, river rock ≈ 1.35 t/yd³. Use "Custom" density if you know the exact figure.',
      'For a driveway, plan on 4-6" total depth with a compacted base course of larger stone under a smaller top course.'
    ]
  },
  'square-footage': {
    intro: 'Compute area of nine common shapes with labelled diagrams — rectangle, border, circle, ring, triangle, trapezoid, sector, parallelogram.',
    about: [
      'For irregular areas, break the shape into rectangles and triangles, compute each separately, then sum. Any triangle can be computed from three edge lengths using Heron\'s formula.',
      'Sector area = ½ · r² · θ, where θ is in radians. Convert degrees → radians by multiplying by π/180.'
    ]
  },
  conversion: {
    intro: 'Convert between 130+ units across 17 families — length, area, volume, mass, temperature, energy, data, fuel economy and more.',
    about: [
      'Behind the scenes, each family stores a canonical SI base unit and every option is a multiplier to that base — so all conversions are O(1) arithmetic with no loss of precision beyond IEEE-754 doubles.',
      'Temperature, angle (turn/gradian) and fuel economy use custom formulas because they are not simple multiplicative conversions.'
    ]
  },
  'ip-subnet': {
    intro: 'Compute the network, broadcast, subnet mask, wildcard, usable range and host count for IPv4 and IPv6 subnets.',
    about: [
      'CIDR notation (Classless Inter-Domain Routing) writes a subnet as address / prefix-length. Every /n subnet has 2^(32-n) IPv4 addresses; 2 are reserved (network + broadcast) so usable hosts = 2^(32-n) − 2.',
      'IPv6 subnets are far larger (128-bit) and rarely need subnetting for host counts — /64 is the standard end-network prefix.'
    ]
  },
  'ohms-law': {
    intro: 'Solve for voltage, current, resistance or power using any two known quantities.',
    about: [
      'Ohm\'s Law: <b>V = I · R</b>. Combined with power: <b>P = V · I = I² · R = V²/R</b>. Any two of V, I, R, P uniquely determine the other two.',
      'In real circuits, resistors have tolerances (typically ±5% or ±1%) and their resistance changes slightly with temperature. Use the temperature coefficient (ppm/K) for precise work.'
    ]
  },
  resistor: {
    intro: 'Decode 3/4/5/6-band resistor colour codes and compute total resistance for series and parallel networks.',
    about: [
      'For 4-band resistors: first two bands = digits, third = multiplier, fourth = tolerance. For 5-band: first three bands = digits, fourth = multiplier, fifth = tolerance. Six-band adds a temperature-coefficient ring.',
      'Resistors in <b>series</b>: R_total = R₁ + R₂ + …  Resistors in <b>parallel</b>: 1 / R_total = 1/R₁ + 1/R₂ + …'
    ]
  },
  'voltage-drop': {
    intro: 'Estimate voltage drop along a copper or aluminium conductor for correct wire sizing.',
    about: [
      'The National Electrical Code recommends voltage drop under 3% on branch circuits and 5% total (feeder + branch). Excess drop causes flickering lights, motor stalling and equipment damage.',
      'Use <b>V_drop = 2 · K · I · L / CM</b> for single-phase runs, or multiply by √3/2 for three-phase. K = 12.9 for copper, 21.2 for aluminium at 75°C.'
    ]
  },
  electricity: {
    intro: 'Estimate energy consumption (kWh) and running cost of any electrical appliance.',
    about: [
      'kWh = power (kW) × time (h). At the U.S. average retail rate of ~$0.16/kWh, running a 1500 W space heater for 8 h costs about $1.92/day, or nearly $60/month.',
      'Stand-by (vampire) loads — chargers, TVs on standby — can add 5-10% to a household bill. Smart power strips eliminate most of it.'
    ]
  },
  bandwidth: {
    intro: 'Estimate download time, convert data units, plan website bandwidth and analyse hosting allowances.',
    about: [
      'Networking speeds are quoted in <b>bits</b> per second (kbps, Mbps, Gbps); file sizes in <b>bytes</b> (KB, MB, GB). Divide bit-rate by 8 to convert to bytes-per-second.',
      'A 1 GB file over 100 Mbps line takes roughly 1 GB × 8 / 100 Mbps = 80 s under perfect conditions — real transfers include TCP overhead and protocol handshakes.'
    ]
  },
  base64: {
    intro: 'Encode text or files to Base64 or decode Base64 back to text.',
    about: [
      'Base64 encodes 3 bytes of binary into 4 ASCII characters using 64 printable symbols. It bloats the payload by ~33% but makes binary safe to transmit through text-only channels (email, JSON, URLs).',
      'For URLs, use URL-safe Base64 which replaces + and / with - and _.'
    ]
  },
  password: {
    intro: 'Generate strong random passwords with configurable character sets and exclusions.',
    about: [
      'Password strength grows with length far faster than with character-set diversity. A 16-character password using only lowercase letters is stronger than an 8-character password using all symbols.',
      'Exclude ambiguous characters (I/l/1, O/0) when passwords will be typed manually; leave them in when auto-copy/paste is guaranteed. Never reuse passwords across sites.'
    ]
  },
  'wind-chill': {
    intro: 'Compute apparent temperature from air temperature and wind speed using the 2001 NWS formula.',
    about: [
      'Wind chill quantifies how much colder your skin feels when convection accelerates heat loss. It only applies when the actual air temperature is at or below 50 °F (10 °C) and wind is over 3 mph.',
      'Below -18 °F wind chill, exposed skin can freeze in under 30 minutes. Below -40 °F, in under 10 minutes.'
    ]
  },
  'heat-index': {
    intro: 'Compute the "feels like" temperature from air temperature and relative humidity (or dew point).',
    about: [
      'The Rothfusz regression combines temperature and humidity to model how the body loses heat through sweat evaporation — hot & humid air feels hotter than dry air of the same temperature.',
      'Heat index ≥ 90 °F is "extreme caution"; ≥ 105 °F is "danger" (heat exhaustion likely); ≥ 130 °F is "extreme danger" (heat stroke imminent).'
    ]
  },
  molarity: {
    intro: 'Solve for molarity, mass or volume of solute using the Molarity = moles / volume relation.',
    about: [
      'Molarity (M) is moles of solute per litre of solution — not per litre of solvent. To prepare a 1 M NaCl solution, dissolve 58.44 g of NaCl and bring the total volume to 1.00 L (do not add 1.00 L of water).',
      'For dilute biological work, <b>millimolar (mM)</b> and <b>micromolar (µM)</b> are more common. 1 mM = 0.001 M.'
    ]
  },
  'molecular-weight': {
    intro: 'Compute the molar mass of any chemical formula — supports parentheses and multi-element groups.',
    about: [
      'The parser accepts standard chemical notation: capitalised element symbols, subscripts as numbers, and parentheses for groups. Example: <b>Ca(OH)₂</b> = 40.078 + 2·(15.999 + 1.008) = 74.093 g/mol.',
      'Atomic weights follow IUPAC 2021 standards. Isotopic variants are not modelled — the value returned is the natural-abundance weighted average.'
    ]
  },
  speed: {
    intro: 'Solve for speed, distance or time — or convert between mph, km/h, m/s, knots and ft/s.',
    about: [
      'The three quantities are related by <b>speed = distance / time</b>. Rearrange to get any one from the other two.',
      'A mach number is speed relative to the speed of sound (≈ 343 m/s at sea level, 20 °C). Mach 1 varies with temperature and altitude.'
    ]
  },
  density: {
    intro: 'Solve for density, mass or volume from any two knowns.',
    about: [
      'Density (ρ) is mass per unit volume: <b>ρ = m / V</b>. Water\'s density is exactly 1 g/cm³ = 1000 kg/m³ at 4 °C, which is why the metric system is anchored to water.',
      'Materials with density < 1 g/cm³ (wood, most oils, ice) float on water; denser materials (rock, steel, gold) sink.'
    ]
  },
  gpa: {
    intro: 'Compute your semester GPA from courses and credits, or your cumulative GPA, or plan the GPA needed next term to reach a goal.',
    about: [
      'GPA = Σ(grade points × credits) / Σ(credits). US grade-point scale is 4.0 (A) to 0.0 (F). Some schools use a 5.0 scale for AP/honors.',
      'To reach a target cumulative GPA, you need <b>(target × total_credits − current × completed_credits) / next_credits</b> in your remaining courses.'
    ]
  },
  grade: {
    intro: 'Compute your weighted class average from assignments — or find the final-exam grade you need to reach a target.',
    about: [
      'Weighted average = Σ(grade × weight) / Σ(weight). Missing assignments count as 0 unless the syllabus says otherwise.',
      'To reach a target overall grade, you need <b>(target × 100 − (100 − final_weight) × current) / final_weight</b> on the final.'
    ]
  },
  time: {
    intro: 'Add or subtract time, compute intervals, project a datetime forward, or evaluate free-form expressions.',
    about: [
      'The <b>Expression</b> tab is the most flexible: type <code>2h 30m + 1h 45m − 15s</code> and it will return the total in days, hours, minutes and seconds.',
      'All tabs handle crossing midnight and negative results gracefully — a negative duration is shown with a leading minus sign.'
    ]
  },
  mileage: {
    intro: 'Estimate fuel usage, total cost, cost per mile and average speed for a trip between two locations.',
    about: [
      'Enter your start and destination — the Google Maps link opens the route so you can pick up the drive distance and time. Fuel economy can be entered in mpg (US or UK), km/L or L/100 km.',
      'Real fuel usage runs ~10-20% higher than the EPA sticker in city traffic. Highway steady-state is usually within 5%. Tick "round trip" to double the mileage automatically.'
    ]
  },
  tip: {
    intro: 'Compute a tip amount and split the bill among any number of guests.',
    about: [
      'US customary tips: 15% for adequate service, 18-20% for good service, 25%+ for exceptional service. Tax and gratuity may already be included on tables of 6 or more — check the bill.',
      'For shared bills, compute the total (bill + tip) then divide by the number of guests. Rounding up removes the awkward penny.'
    ]
  },

  // ————— More tools —————
  amortization: {
    intro: 'Build a full amortization schedule showing month-by-month principal, interest and balance.',
    about: [
      'In an amortized loan, the payment stays constant but the split between principal and interest changes. The interest portion shrinks each month as the balance falls.',
      'Extra payments applied to principal shorten the loan and drastically reduce total interest — the earlier they are made, the greater the impact.'
    ]
  },
  refinance: {
    intro: 'Compare your current mortgage to a new refinance offer — see savings and break-even.',
    about: [
      'Refinancing replaces the current loan with a new one at a different rate or term. Closing costs typically run 2-5% of the loan balance.',
      'Break-even = closing costs / monthly savings. If you plan to stay past the break-even point, the refinance pays off.'
    ]
  },
  apr: {
    intro: 'Compare loan offers apples-to-apples by including all fees in the APR.',
    about: [
      'Interest rate is the cost of borrowing the principal. APR (annual percentage rate) includes origination fees, points and other costs — always a better comparison figure.',
      'For adjustable-rate loans, APR only reflects the initial rate; the future rate is unknown.'
    ]
  },
  'compound-interest': {
    intro: 'See how compound interest grows a principal over time — the eighth wonder of the world.',
    about: [
      'Compound interest earns interest on both the original principal and previously accumulated interest. Formula: <b>A = P(1 + r/n)^(nt)</b>.',
      'The more frequently interest compounds, the higher the final balance. Continuous compounding gives <b>A = Pe^(rt)</b>.'
    ]
  },
  'interest-rate': {
    intro: 'Solve for the interest rate that produces a given monthly payment.',
    about: [
      'When a lender advertises only the payment and term, this calculator recovers the implied interest rate using a numerical bisection method.',
      'Always ask for the APR to compare offers fairly — hidden fees can raise the effective rate significantly above the nominal rate.'
    ]
  },
  investment: {
    intro: 'Project investment growth with contributions, compounding and rate of return.',
    about: [
      'Regular contributions dramatically outperform lump-sum investments over long horizons because of dollar-cost averaging and additional principal.',
      'Long-run US stock-market returns are around 7% real (10% nominal minus 3% inflation). Bond returns are lower but less volatile.'
    ]
  },
  retirement: {
    intro: 'Estimate the nest-egg you need and whether current savings are on track.',
    about: [
      'The <b>4% rule</b> suggests you can safely withdraw 4% of your portfolio per year in retirement — implying you need 25× your annual expenses saved.',
      'The <b>50/30/20 rule</b> allocates income to needs, wants and savings — with at least 15% of gross income to retirement from your 20s.'
    ]
  },
  '401k': {
    intro: 'Project your 401(k) balance to retirement with contribution rate, employer match and market returns.',
    about: [
      'Contribute at least enough to earn the full employer match — it is free money. In 2026 the IRS elective-deferral limit is $23,500 ($31,000 age 50+).',
      'Traditional 401(k) reduces taxable income today; Roth 401(k) uses after-tax dollars but grows tax-free.'
    ]
  },
  budget: {
    intro: 'Build a monthly budget across housing, food, transport, savings and discretionary categories.',
    about: [
      'The <b>50/30/20</b> guideline: 50% needs (rent, groceries, utilities), 30% wants, 20% savings & debt-payoff.',
      'Track for a month before setting targets — most people underestimate discretionary spending by 20-40%.'
    ]
  },
  'height': {
    intro: 'Predict a child\'s adult height from parents\' heights and current growth data.',
    about: [
      'The Tanner mid-parental method: adult_boy = (father + mother + 5")/2, adult_girl = (father + mother − 5")/2. Typical error ±3 inches.',
      'Height is 60-80% heritable but nutrition, sleep and childhood illness significantly affect the outcome.'
    ]
  },
  'bra-size': {
    intro: 'Find your correct band and cup size and convert across US, UK, EU, FR, IT and AU standards.',
    about: [
      'Measure underbust snugly around the ribcage and bust loosely over the fullest part. Band size ≈ underbust rounded to the nearest even inch; cup letter comes from the bust − band difference (1" = A, 2" = B, 3" = C, 4" = D …).',
      'Sizes vary between manufacturers by up to a full cup, so always try before committing.'
    ]
  },
  'shoe-size': {
    intro: 'Convert shoe sizes across US, UK, EU, JP and CM — for men, women, kids and infants.',
    about: [
      'Foot length is the most reliable input: stand on paper, mark heel and longest toe, measure. Sizing conventions differ significantly across manufacturers.',
      'Kids\' feet grow ~1 size every 3-4 months in early years; check fit monthly.'
    ]
  },
  'roman-numeral': {
    intro: 'Convert decimal ↔ Roman numerals — with vinculum notation supporting values up to 3,999,999.',
    about: [
      'Roman numerals use seven symbols: I(1), V(5), X(10), L(50), C(100), D(500), M(1000). A smaller symbol before a larger one is subtracted (IV = 4).',
      '[N] in vinculum notation means N × 1000. So [X] = 10,000 and [XI]CDLXXXIX = 11,489.'
    ]
  },
  'day-of-week': {
    intro: 'Find the day of the week (and day/week of year) for any historical or future date.',
    about: [
      'The Gregorian calendar was introduced in 1582 by Pope Gregory XIII, replacing the Julian calendar. Dates before Oct 15, 1582 in the Gregorian calendar are extrapolated ("proleptic").',
      'ISO 8601 defines the week starting on Monday, with week 1 being the week containing the first Thursday of the year.'
    ]
  },
  'dice-roller': {
    intro: 'Roll standard or custom N-sided dice, view individual values and the total.',
    about: [
      'The generator uses Math.random() — pseudo-random and NOT cryptographically secure. Adequate for board games and casual use, not for gambling.',
      'Common polyhedral dice: d4, d6, d8, d10, d12, d20. Non-standard sides let you simulate any weighted or unusual die.'
    ]
  },
  gdp: {
    intro: 'Compute Gross Domestic Product using the Expenditure or Income (Resource Cost) approach.',
    about: [
      '<b>Expenditure approach:</b> Y = C + I + G + (X − M). Consumption, investment, government spending, plus net exports.',
      '<b>Income approach:</b> Y = wages + rent + interest + profits + statistical adjustments. In theory both approaches give the same total.'
    ]
  },
  'golf-handicap': {
    intro: 'Compute your Handicap Index from recent rounds or convert an Index to a Course Handicap.',
    about: [
      'Score differential = (adjusted gross score − course rating) × 113 / slope rating. Handicap Index = average of best differentials × 0.96.',
      'Course Handicap = Index × (slope / 113) + (course rating − par). Round to the nearest whole number for match play.'
    ]
  },
  'engine-horsepower': {
    intro: 'Estimate engine horsepower from 1/4-mile Elapsed Time or Trap-Speed.',
    about: [
      '<b>ET method (Hale/Fox):</b> HP = weight / (ET / 5.825)³. Best for cars in the 8-16 second range.',
      '<b>Trap-Speed method (Huntington):</b> HP = weight × (mph / 234)³. Slightly more accurate for high-powered cars.'
    ]
  },
  horsepower: {
    intro: 'Convert torque × RPM to horsepower, or use force/distance/time definition, or convert HP units.',
    about: [
      'HP = torque × RPM / 5252 (imperial) — the definition of 1 HP is the power to lift 550 lb by 1 ft in 1 second.',
      'One mechanical HP = 745.7 W. Metric HP (PS) = 735.5 W. Boiler HP = 9.81 kW.'
    ]
  },
  btu: {
    intro: 'Estimate BTU cooling / heating needs for a room or general HVAC sizing.',
    about: [
      'Rule of thumb: 20 BTU per sq ft for mild climates, 30-35 for hot climates. Sun exposure, insulation and occupant count all modify the base.',
      '1 kW = 3412 BTU/h. 1 ton of cooling = 12,000 BTU/h.'
    ]
  },
  'fuel-cost': {
    intro: 'Estimate fuel cost for any trip with multiple distance, efficiency and price units.',
    about: [
      'Fuel efficiency degrades with speed above 55 mph; using cruise control on the highway typically improves it 5-10%.',
      'Round-trip mode doubles the distance for you. All efficiency inputs are converted to mpg internally before pricing.'
    ]
  },
  'gas-mileage': {
    intro: 'Compute fuel economy from distance and fuel used — mpg, km/L and L/100 km.',
    about: [
      'Track over full tanks for accuracy — trip meters + fuel bill divided by miles is the most reliable field measure.',
      'A well-tuned modern car achieves 30-40 mpg on the highway; hybrids 45-55+.'
    ]
  },
  'wind-chill': {
    intro: 'Compute apparent temperature from air temperature and wind speed using the 2001 NWS formula.',
    about: [
      'Wind chill quantifies how much colder your skin feels when convection accelerates heat loss. Only applies at temperatures ≤ 50 °F (10 °C) and wind > 3 mph.',
      'Below -18 °F wind chill, exposed skin can freeze in under 30 minutes.'
    ]
  },
  'dew-point': {
    intro: 'Compute dew point from air temperature and relative humidity.',
    about: [
      'Dew point is the temperature to which air must be cooled at constant pressure for saturation. When the ground cools to the dew point, water condenses — you get dew or fog.',
      'A dew point above 65 °F feels muggy; above 75 °F is oppressive.'
    ]
  },
  'time-zone': {
    intro: 'Convert a date and time between any two named time zones.',
    about: [
      'Time zones are offsets from UTC (Coordinated Universal Time). Some regions use half-hour or 45-minute offsets — India Standard Time is UTC+5:30, Nepal is UTC+5:45.',
      'This tool uses a static offset — daylight saving time is not automatically applied. Verify locally during summer months.'
    ]
  },
  'time-card': {
    intro: 'Weekly work-hours tracker with rounding, breaks, base pay, overtime and printable reports.',
    about: [
      'Common US overtime rules: FLSA mandates 1.5× base pay after 40 hours per week for non-exempt employees. California and a few states additionally require 1.5× after 8 hours per day, 2× after 12.',
      'Round-time options (5/10/15/30 min) match common payroll conventions.'
    ]
  },
  'time-duration': {
    intro: 'Compute a duration between two times or between two dates.',
    about: [
      'The Between Two Dates tab handles arbitrary spans and reports totals in seconds, minutes, hours, days, weeks and approximate months/years.',
      'Months and years are approximated (30.44 and 365.25 days respectively) since neither has a fixed length.'
    ]
  },
  ovulation: {
    intro: 'Estimate your fertile window and next likely ovulation date from cycle history.',
    about: [
      'For a typical 28-day cycle, ovulation occurs around day 14. The fertile window spans the 5 days before ovulation and the day of ovulation itself.',
      'Actual timing varies — track basal body temperature, LH surge, and cervical mucus for higher confidence.'
    ]
  },
  pregnancy: {
    intro: 'Estimate the current gestational week and progress by trimester.',
    about: [
      'Gestational age is measured from the first day of the last menstrual period (LMP). Conception typically occurs ~2 weeks later, so a "6-week pregnancy" is really 4 weeks post-conception.',
      'Full-term pregnancy is 37-42 weeks. Trimester 1: weeks 1-12, T2: 13-27, T3: 28-40.'
    ]
  },
  'due-date': {
    intro: 'Estimate your baby\'s due date using Naegele\'s rule or ultrasound dating.',
    about: [
      'Naegele\'s rule: LMP + 280 days ≈ due date. Only ~4% of babies actually arrive on the exact due date; most arrive within ±2 weeks.',
      'First-trimester ultrasound is the most accurate dating method, accurate to ±5 days.'
    ]
  },
  'ideal-weight': {
    intro: 'Estimate ideal body weight from height using Devine, Robinson, Miller and Hamwi formulas.',
    about: [
      'Different formulas give slightly different results — the "ideal" for a specific person depends on frame size and body composition, not just height.',
      'For muscular or athletic builds, body-fat percentage is a much better health indicator than weight.'
    ]
  },
  matrix: {
    intro: 'Perform matrix arithmetic — add, subtract, multiply, transpose, inverse, determinant and rank.',
    about: [
      'Matrices form the backbone of linear algebra, used everywhere from 3D graphics to machine learning.',
      'A matrix is invertible if and only if its determinant is non-zero.'
    ]
  },

  // ————— Extended coverage (Batch 6) —————
  'auto-loan': {
    intro: 'Estimate car-loan monthly payment including sales tax, fees, trade-in and down payment.',
    about: [
      'Auto loans are secured by the vehicle. Typical terms run 36-84 months; shorter terms mean less interest but higher monthly payments.',
      'Watch for negative equity: a $30k car financed with $0 down can be worth less than the loan balance within months, exposing you to loss if the car is totalled. Gap insurance or a bigger down payment mitigates this.'
    ]
  },
  'rental-property': {
    intro: 'Analyse a rental investment — cash flow, cap rate, cash-on-cash return and internal rate of return.',
    about: [
      'Capitalization rate = net operating income (NOI) / purchase price. It ignores financing and lets you compare properties on income potential alone.',
      'Cash-on-cash return = annual cash flow / total cash invested. IRR accounts for the time value of money over the full holding period including sale proceeds.'
    ]
  },
  'real-estate': {
    intro: 'Compare buy-vs-rent, project cash flow and estimate closing costs on a real-estate deal.',
    about: [
      'The 1% rule: monthly rent should equal at least 1% of purchase price to cover expenses in most markets. The 50% rule: operating expenses typically consume 50% of gross rent.',
      'Consider vacancy (5-10%), repairs (5-15%), management (8-12%) and reserves when running numbers.'
    ]
  },
  'rent-vs-buy': {
    intro: 'Compare the total cost of renting vs buying a home over a given horizon.',
    about: [
      'Buying wins if you stay long enough to amortize closing costs (typically 5+ years). Renting wins if you may relocate or if home prices are stagnant.',
      'Home equity grows through both principal payments and appreciation; renting builds none of that.'
    ]
  },
  rent: {
    intro: 'Determine how much rent you can afford based on income and existing debt.',
    about: [
      'Rule of thumb: spend no more than 30% of gross income on rent. Some cities (SF, NYC) push this to 40%+, leaving little for savings and other essentials.',
      'Landlords typically require 3× the monthly rent in annual gross income before approving an application.'
    ]
  },
  'down-payment': {
    intro: 'Estimate the down payment needed and see how it affects your monthly mortgage.',
    about: [
      '20% down avoids PMI (private mortgage insurance) on conventional loans. FHA loans allow 3.5%, VA loans 0%.',
      'A larger down payment reduces the loan size, monthly payment and lifetime interest — but leaves less liquid savings for emergencies.'
    ]
  },
  'house-affordability': {
    intro: 'Find the home price you can afford based on income, debts and down payment.',
    about: [
      'Lenders use the 28/36 rule: housing costs should not exceed 28% of gross monthly income; total debt payments not more than 36%.',
      'Include property tax, insurance and HOA fees in the calculation — they can add 15-30% on top of principal & interest.'
    ]
  },
  refinance: {
    intro: 'Compare a refinance offer to your current mortgage — savings and break-even.',
    about: [
      'Refinancing is worth it if you plan to stay past the break-even point (closing costs / monthly savings). Rates dropping 0.75%+ typically make the math work.',
      'Cash-out refis let you tap equity but restart the amortization clock, meaning more interest overall unless invested wisely.'
    ]
  },
  'annuity': {
    intro: 'Calculate the future value or income stream of an annuity — ordinary or due.',
    about: [
      'An annuity is a series of equal payments over time. Present value tells you what future payments are worth today; future value tells you how a stream will grow.',
      'Fixed annuities guarantee a rate; variable annuities depend on market performance. Fees can be steep — read the prospectus carefully.'
    ]
  },
  'annuity-payout': {
    intro: 'Estimate how much you can withdraw periodically from an annuity, or how long a fixed withdrawal will last.',
    about: [
      'The distribution (payout) phase of an annuity converts your accumulated balance into a stream of income. This is the inverse of the accumulation phase.',
      'Fixed length: dial in years of payout and the calculator computes the periodic withdrawal. Fixed payment: dial in a target withdrawal and the calculator computes how long the fund lasts.',
      'If your target withdrawal is below the periodic return earned by the balance, the annuity will never fully deplete — the fund will keep growing.'
    ]
  },
  'mutual-fund': {
    intro: 'Project a mutual fund\'s ending balance and net IRR after sales loads, deferred charges and operating expenses.',
    about: [
      'Front-end sales loads reduce every dollar you invest. Operating expenses (the expense ratio) are deducted continuously from the fund\'s NAV. Back-end (deferred) sales charges apply on redemption.',
      'Small differences in fees compound dramatically. A 1% expense ratio can consume 20-30% of long-term returns.',
      'Compare share classes: A-shares carry a front-end load; B/C-shares carry deferred/level loads. Index funds and ETFs typically minimize both.'
    ]
  },
  'cash-back': {
    intro: 'Compare a manufacturer cash-back rebate against a low-interest financing offer on an auto loan.',
    about: [
      'Auto dealers often force a choice between a cash rebate and a promotional low-APR loan. Which is better depends on the loan amount, term, and the spread between the "regular" and promotional rates.',
      'The calculator computes total cost (principal + interest + tax + fees − rebate/trade-in) for each scenario and flags the cheaper one.',
      'Rebates can also be applied to the down payment to shrink the financed principal — small but often overlooked wrinkle.'
    ]
  },
  bond: {
    intro: 'Compute bond price, yield to maturity, current yield and duration.',
    about: [
      'Bond prices move inversely to interest rates. When rates rise, existing bonds with lower coupons become less attractive and their prices fall.',
      'Yield to maturity (YTM) is the total return anticipated if the bond is held to maturity — the internal rate of return of all cash flows.'
    ]
  },
  ira: {
    intro: 'Compare Traditional and Roth IRA contributions and project growth to retirement.',
    about: [
      'Traditional IRA contributions are tax-deductible today; withdrawals in retirement are taxed as income. Roth IRA is the opposite: after-tax dollars in, tax-free withdrawals.',
      'The 2026 contribution limit is $7,000 ($8,000 age 50+). Higher earners may face Roth income limits or have their traditional deduction phased out.'
    ]
  },
  'roth-ira': {
    intro: 'Project Roth IRA growth to retirement — all withdrawals tax-free.',
    about: [
      'Roth IRAs use after-tax dollars but grow and are withdrawn tax-free after age 59½. Contributions (not earnings) can be withdrawn any time without penalty.',
      'Income limits apply: single filers making over $161k in 2026 cannot contribute directly to a Roth (though a "backdoor" via conversion is possible).'
    ]
  },
  'social-security': {
    intro: 'Estimate your monthly Social Security benefit based on earnings and claim age.',
    about: [
      'Full retirement age (FRA) is 66-67 depending on birth year. Claiming at 62 reduces benefits by ~30%; delaying to 70 boosts them by ~24%.',
      'Benefits are based on your 35 highest-earning years, indexed for inflation. Missing years count as zeros — work at least 35 years to maximize.'
    ]
  },
  pension: {
    intro: 'Project a defined-benefit pension using service years, salary and multiplier.',
    about: [
      'Typical formula: monthly pension = years of service × multiplier (usually 1.5%-2.5%) × final average salary.',
      'Ask your plan for cost-of-living adjustments (COLA), spousal survivor benefits, and lump-sum vs annuity payout options — each has major tax implications.'
    ]
  },
  'income-tax': {
    intro: 'Estimate US federal income tax using the current marginal brackets.',
    about: [
      'US tax is progressive: only income above each bracket threshold is taxed at that bracket\'s rate. Your <b>effective</b> tax rate is total tax / total income, always lower than your top marginal rate.',
      'Standard deduction, itemized deductions and above-the-line adjustments (401k, HSA) reduce taxable income before brackets are applied.'
    ]
  },
  'sales-tax': {
    intro: 'Add sales tax to a price, or extract the pre-tax amount from a tax-inclusive total.',
    about: [
      'US sales-tax rates vary by state (0-10%+) and locality. Some states exempt groceries and clothing.',
      'Value-added tax (VAT) used in most of the world applies at every stage of production; sales tax applies only at retail.'
    ]
  },
  vat: {
    intro: 'Add or remove Value-Added Tax at any rate.',
    about: [
      'VAT is levied on the value added at each production stage but ultimately borne by the end consumer. Standard rates: UK 20%, EU average ~20%, India GST 5-28% by category.',
      'Businesses reclaim VAT paid on inputs, so only the "value added" is taxed at each step.'
    ]
  },
  'take-home-pay': {
    intro: 'Estimate net pay after federal, state, FICA and other typical deductions.',
    about: [
      'Gross pay minus taxes, retirement contributions and benefit premiums = net (take-home) pay. FICA alone is 7.65% (Social Security 6.2% + Medicare 1.45%) up to the SS wage base.',
      'Contribute to pre-tax 401(k) and HSA to lower taxable income and effective rate.'
    ]
  },
  salary: {
    intro: 'Convert between hourly, weekly, monthly and annual salary rates.',
    about: [
      'US convention assumes 2,080 working hours per year (40 × 52). Some employers use 2,087 (40 × 52.18) to account for the extra day in most years.',
      'When comparing offers, factor in benefits: PTO, health premium, 401(k) match, bonuses. A $95k with 6% match can beat $100k without.'
    ]
  },
  'marriage-tax': {
    intro: 'Compare filing single vs married-filing-jointly to spot marriage penalty or bonus.',
    about: [
      'A "marriage penalty" arises when two similar high incomes push the joint filer into a higher bracket than they would individually. A "marriage bonus" arises when one earns much less.',
      'The 2018 TCJA widened brackets to eliminate most penalties for the middle class, but they persist at high incomes and around income-based phase-outs.'
    ]
  },
  'estate-tax': {
    intro: 'Estimate federal estate tax due on a decedent\'s estate.',
    about: [
      'The federal estate-tax exemption is $13.99M per individual (2026). Assets above the exemption are taxed at 40%.',
      'Portability lets a surviving spouse use any unused exemption from the deceased spouse, effectively doubling the exemption for a couple.'
    ]
  },
  'credit-card': {
    intro: 'Estimate how long it will take to pay off a credit-card balance and how much interest you\'ll pay.',
    about: [
      'Minimum payments (typically 1-3% of balance) barely cover interest — a $5,000 balance at 22% APR takes 20+ years to pay off at minimum only.',
      'Balance transfers to 0% APR cards can save thousands, but factor in the balance-transfer fee (3-5%) and payoff plan before the promo rate ends.'
    ]
  },
  'credit-cards-payoff': {
    intro: 'Plan how to pay off multiple credit cards — avalanche (highest APR first) vs snowball (smallest balance first).',
    about: [
      '<b>Avalanche</b> pays highest-interest debts first and mathematically saves the most money. <b>Snowball</b> pays smallest balances first for psychological wins.',
      'Studies show snowball\'s early wins keep people motivated to complete payoff even though it costs slightly more interest.'
    ]
  },
  'debt-consolidation': {
    intro: 'Combine multiple debts into a single loan with one payment.',
    about: [
      'Consolidation can lower total interest and simplify payments but only helps if the consolidation loan rate is below the weighted average of your existing debts.',
      'Beware fees, prepayment penalties and — most importantly — running the credit cards back up after consolidating them.'
    ]
  },
  'debt-payoff': {
    intro: 'Plan your debt payoff with a target date or extra-payment amount.',
    about: [
      'Adding even $50/month to your minimum payment can shave years off the payoff and thousands in interest.',
      'Tackle high-interest debt first (avalanche) or smallest balances first (snowball) — both work, whichever keeps you motivated.'
    ]
  },
  'debt-to-income': {
    intro: 'Compute your debt-to-income ratio — a key lending metric.',
    about: [
      'DTI = monthly debt payments / monthly gross income. Lenders generally cap total DTI at 43% (higher for some FHA loans), with housing at 28%.',
      'Lowering DTI (by paying down debt or increasing income) is one of the fastest ways to improve loan approval odds.'
    ]
  },
  discount: {
    intro: 'Compute discount amounts, sale prices and percent savings.',
    about: [
      '"30% off" means new_price = original × 0.70. Sequential discounts stack multiplicatively: 20% off then 10% off is 0.8 × 0.9 = 28% off, not 30%.',
      'Compare with unit price to spot the best deal — "buy one get one 50% off" is really 25% off, not 50%.'
    ]
  },
  commission: {
    intro: 'Compute sales commission at flat or tiered rates.',
    about: [
      'Straight commission = sales × rate. Tiered plans have accelerators (e.g. 5% to quota, 8% over quota). Draws are advances against future commission — repaid if not earned.',
      'Total on-target earnings (OTE) is base salary + expected commission at 100% of quota.'
    ]
  },
  'business-loan': {
    intro: 'Commercial financing for equipment, working capital, real estate or expansion.',
    about: [
      'Business loans can be amortized, interest-only, or partially amortized with a balloon. Rates depend on the business\'s credit, revenue and collateral.',
      'SBA-guaranteed loans (7(a), 504) offer longer terms and lower down payments but require extensive paperwork.'
    ]
  },
  'apr': {
    intro: 'Compare loan offers apples-to-apples by including all fees in the APR.',
    about: [
      'APR includes origination fees and points; the nominal interest rate does not. Always use APR to compare offers.',
      'For adjustable-rate loans, APR reflects the initial rate only — future rate changes are unknown.'
    ]
  },
  interest: {
    intro: 'Compute simple or compound interest for savings, loans or investments.',
    about: [
      'Simple interest: I = P·r·t (interest only on principal). Compound interest: A = P(1 + r/n)^(nt) (interest on interest).',
      'The Rule of 72 estimates doubling time: 72 / rate% = years. At 6%, money doubles in ~12 years.'
    ]
  },
  'college-cost': {
    intro: 'Project the total cost of college given tuition, fees and inflation.',
    about: [
      'Tuition inflation historically runs 5-6%/year, twice general inflation. A newborn today could face $500k+ for a private 4-year degree.',
      '529 plans are the standard tax-advantaged savings vehicle — earnings grow tax-free if used for qualified education expenses.'
    ]
  },
  'depreciation': {
    intro: 'Compute asset depreciation using straight-line, declining-balance or MACRS methods.',
    about: [
      'Straight-line spreads depreciation evenly over useful life. Double-declining-balance accelerates deductions.',
      'For tax purposes, use IRS MACRS class lives: 5 years for cars, 7 years for office equipment, 27.5 years for residential real estate.'
    ]
  },
  'inflation': {
    intro: 'Adjust dollar amounts for inflation using CPI to compare purchasing power over time.',
    about: [
      'CPI (Consumer Price Index) tracks price changes for a basket of goods over time. $1 in 1980 has about the buying power of $3.75 today.',
      'Long-run US inflation averages ~3%. High-inflation periods (1970s, 2022) drastically erode fixed-income returns.'
    ]
  },
  irr: {
    intro: 'Compute the internal rate of return of a series of cash flows.',
    about: [
      'IRR is the discount rate that makes NPV equal zero. It answers: what compounded annual return does this investment produce?',
      'IRR can be misleading for non-conventional cash flows (multiple sign changes); MIRR fixes some of its shortcomings.'
    ]
  },
  roi: {
    intro: 'Compute return on investment — total gain vs cost.',
    about: [
      'ROI = (final − initial) / initial × 100%. Simple but ignores time — a 20% ROI over 1 year is very different from over 10 years.',
      'For time-adjusted comparison, use CAGR: (final/initial)^(1/years) − 1.'
    ]
  },
  'average-return': {
    intro: 'Compute average investment return — arithmetic vs geometric (CAGR).',
    about: [
      'Arithmetic mean = simple average. Geometric mean (CAGR) accounts for compounding and is always ≤ arithmetic.',
      'For a series like +50%, -50%: arithmetic mean is 0% but geometric is -13.4% — the actual outcome from $100 → $150 → $75.'
    ]
  },
  'sample-size': {
    intro: 'Compute the sample size needed for a survey at a given margin of error and confidence.',
    about: [
      'For proportions: n = z² · p(1−p) / e². Assume p = 0.5 for the most conservative (largest) sample.',
      'A 95% confidence level uses z ≈ 1.96. Reducing margin of error from 5% to 2.5% quadruples the required sample.'
    ]
  },
  'confidence-interval': {
    intro: 'Compute confidence intervals for means or proportions.',
    about: [
      'A 95% CI means: if we repeated the sampling process many times, 95% of the intervals would contain the true parameter.',
      'Wider intervals = less precision. Larger sample sizes shrink the interval width by √n.'
    ]
  },
  'z-score': {
    intro: 'Compute z-scores and probabilities from a standard normal distribution.',
    about: [
      'Z = (x − μ) / σ tells you how many standard deviations x lies from the mean.',
      '68/95/99.7 rule: 68% of values within ±1σ, 95% within ±2σ, 99.7% within ±3σ.'
    ]
  },
  'p-value': {
    intro: 'Compute p-values for t, z or chi-square tests.',
    about: [
      'P-value is the probability of observing a test statistic at least as extreme as the observed one, assuming H₀ is true.',
      'p < 0.05 is conventionally "significant" but this threshold is arbitrary — always report the exact p-value and effect size.'
    ]
  },
  probability: {
    intro: 'Compute the probability of independent, mutually exclusive or complementary events.',
    about: [
      'P(A and B) = P(A) × P(B|A). If A and B are independent, P(B|A) = P(B).',
      'P(A or B) = P(A) + P(B) − P(A and B). For mutually exclusive events, the last term is 0.'
    ]
  },
  permutation: {
    intro: 'Compute permutations (order matters) and combinations (order doesn\'t).',
    about: [
      'P(n,r) = n! / (n−r)! · C(n,r) = n! / (r!(n−r)!).',
      'The number of ways to seat 5 people at a table is 5! = 120 (permutations). The number of 5-card poker hands is C(52,5) = 2,598,960.'
    ]
  },
  slope: {
    intro: 'Compute the slope and equation of a line through two points.',
    about: [
      'Slope m = (y₂ − y₁) / (x₂ − x₁). Point-slope form: y − y₁ = m(x − x₁). Slope-intercept form: y = mx + b.',
      'Parallel lines share the same slope. Perpendicular lines have slopes m₁ · m₂ = −1.'
    ]
  },
  pace: {
    intro: 'Compute running pace, time or distance from any two of them — plus common race distances.',
    about: [
      'Pace = time / distance. Common metric is minutes per mile or minutes per kilometre.',
      'A 4-hour marathon requires roughly 9:09 min/mile. A sub-3-hour marathon requires 6:52 min/mile — elite territory.'
    ]
  },
  age: {
    intro: 'Compute exact age in years, months, days and hours between any two dates.',
    about: [
      'Age is computed by subtracting the birth date from today, accounting for month/day boundaries.',
      'Different cultures count age differently: East Asian traditional age adds 1 at birth and again every Lunar New Year.'
    ]
  },
  countdown: {
    intro: 'Live countdown to a target date and time.',
    about: [
      'The countdown updates every second — precise to the millisecond in the underlying calculation.',
      'Time zones can create confusion — set the target date in your local zone and the tool will handle DST transitions correctly.'
    ]
  },
  'day-counter': {
    intro: 'Count the exact number of days between two calendar dates.',
    about: [
      'Both start and end dates are included when explicitly selected. Business-day count excludes Saturday, Sunday and (optionally) national holidays.',
      'Useful for interest calculations, project timelines and pregnancy tracking.'
    ]
  },
  'half-life': {
    intro: 'Compute radioactive decay, remaining substance or elapsed time using half-life.',
    about: [
      'Half-life is the time for half of a radioactive isotope to decay. Formula: N(t) = N₀ · (1/2)^(t/T½).',
      'Carbon-14 (T½ ≈ 5,730 years) is used for archaeological dating. Uranium-238 (T½ ≈ 4.5 billion years) dates the Earth.'
    ]
  },
  fraction: {
    intro: 'Add, subtract, multiply and divide fractions and mixed numbers with step-by-step working.',
    about: [
      'To add or subtract fractions, find a common denominator. To multiply, multiply numerators and denominators. To divide, invert and multiply.',
      'Simplify by dividing top and bottom by their GCD. Improper fractions convert to mixed numbers.'
    ]
  },
  gcf: {
    intro: 'Compute the greatest common factor (GCF/GCD) of two or more integers.',
    about: [
      'The GCF is the largest positive integer that divides every input without remainder. Compute using the Euclidean algorithm.',
      'GCF(a, b) × LCM(a, b) = a × b — a useful identity.'
    ]
  },
  lcm: {
    intro: 'Compute the least common multiple (LCM) of two or more integers.',
    about: [
      'LCM is the smallest positive integer divisible by every input.',
      'Used for finding common denominators, scheduling repeating events, and gear-ratio problems.'
    ]
  },
  factor: {
    intro: 'Factor an integer into its prime factors.',
    about: [
      'Every integer > 1 has a unique factorization into primes (Fundamental Theorem of Arithmetic).',
      'Factoring gets exponentially harder as numbers grow — this hardness underpins RSA cryptography.'
    ]
  },
  binary: {
    intro: 'Convert integers between binary, octal, decimal and hexadecimal.',
    about: [
      'Binary uses base 2 (digits 0-1); octal base 8 (0-7); hex base 16 (0-9,A-F). Every 4 binary digits map to one hex digit — that\'s why hex is popular in computing.',
      'Two\'s complement is how signed integers are represented in binary: the top bit indicates sign.'
    ]
  },
  hex: {
    intro: 'Convert numbers to and from hexadecimal, and perform hex arithmetic.',
    about: [
      'Hex (base 16) is a compact way to represent binary — every hex digit encodes 4 bits.',
      'Colors are often expressed as hex triplets (#FF6600 = R:255, G:102, B:0) because each pair fits one byte.'
    ]
  },
  logarithm: {
    intro: 'Compute logarithms base 10, e (ln), 2 or any custom base.',
    about: [
      'log_b(x) is the exponent y such that b^y = x. Ln (natural log) uses e ≈ 2.71828. Log-scale plots reveal exponential trends as straight lines.',
      'Change of base: log_a(x) = log_b(x) / log_b(a). Useful for computing arbitrary-base logs from ln or log₁₀.'
    ]
  },
  root: {
    intro: 'Compute square, cube and n-th roots of any real number.',
    about: [
      'Roots are the inverse of powers: √x is the number that, when squared, gives x. Complex numbers are needed for even roots of negatives.',
      'Newton\'s method converges quadratically to any real root of a differentiable function.'
    ]
  },
  quadratic: {
    intro: 'Solve quadratic equations of the form ax² + bx + c = 0 with real or complex roots.',
    about: [
      'Quadratic formula: x = (−b ± √(b² − 4ac)) / (2a). The discriminant b² − 4ac reveals the nature of roots: positive → two real, zero → one, negative → complex.',
      'Completing the square, factoring and the quadratic formula are three routes to the same answer.'
    ]
  },
  'right-triangle': {
    intro: 'Solve right triangles — find sides and angles given any two knowns.',
    about: [
      'Pythagoras: a² + b² = c². Angles: sin θ = opposite/hypotenuse; cos θ = adjacent/hypotenuse; tan θ = opposite/adjacent.',
      'The 3-4-5, 5-12-13 and 8-15-17 Pythagorean triples are useful shortcuts in construction and design.'
    ]
  },
  triangle: {
    intro: 'Solve any triangle given three inputs — SSS, SAS, ASA, SSA or AAS.',
    about: [
      'Law of Sines: a/sin A = b/sin B = c/sin C. Law of Cosines: c² = a² + b² − 2ab·cos C.',
      'SSA cases can be ambiguous — two, one or zero triangles can satisfy the given data.'
    ]
  },
  circle: {
    intro: 'Compute circumference, area, diameter or radius of a circle from any one input.',
    about: [
      'Area = π·r². Circumference = 2π·r = π·d. π ≈ 3.14159265358979.',
      'The ratio of circumference to diameter is π for every circle — one of mathematics\' most famous constants.'
    ]
  },
  volume: {
    intro: 'Compute the volume of common 3D shapes — cube, sphere, cylinder, cone, pyramid, prism.',
    about: [
      'Cube: s³. Sphere: (4/3)πr³. Cylinder: πr²h. Cone: (1/3)πr²h. Pyramid: (1/3)·base·h.',
      'Any prism (uniform cross-section) has volume = base area × height. Any pyramid or cone has one-third of the corresponding prism/cylinder.'
    ]
  },
  area: {
    intro: 'Compute area of common 2D shapes — rectangle, triangle, circle, trapezoid, parallelogram, sector.',
    about: [
      'Rectangle: L·W. Triangle: ½·b·h. Circle: π·r². Trapezoid: ½·(a+b)·h.',
      'Any polygon can be triangulated and the areas summed.'
    ]
  },
  'surface-area': {
    intro: 'Compute the surface area of common 3D shapes.',
    about: [
      'Sphere: 4π·r². Cube: 6·s². Cylinder: 2π·r² + 2π·r·h (top+bottom+side).',
      'Surface area determines heat exchange, drag and material cost — critical in engineering design.'
    ]
  },
  distance: {
    intro: 'Compute the distance between two points in 2D or 3D.',
    about: [
      '2D: √((x₂−x₁)² + (y₂−y₁)²). 3D: extend with a z-term. Great-circle distance uses the Haversine formula on Earth\'s surface.',
      'Manhattan distance is the sum of absolute differences — used in grid/city routing.'
    ]
  },
  average: {
    intro: 'Compute mean, median, mode, range and other descriptive statistics.',
    about: [
      'Mean = sum / count. Median = middle value when sorted. Mode = most frequent value. Each has strengths — mean is sensitive to outliers, median is robust.',
      'Skewness measures asymmetry; kurtosis measures tail-heaviness.'
    ]
  },
  'number-sequence': {
    intro: 'Find the n-th term or sum of arithmetic and geometric sequences.',
    about: [
      'Arithmetic: aₙ = a₁ + (n-1)·d. Sum = n·(a₁ + aₙ)/2.',
      'Geometric: aₙ = a₁·r^(n-1). Sum = a₁·(1 − rⁿ)/(1 − r) for r ≠ 1.'
    ]
  },
  ratio: {
    intro: 'Simplify ratios and solve for missing terms in proportions.',
    about: [
      'A ratio compares quantities: 3:4 means 3 parts to 4 parts. Simplify by dividing both sides by their GCD.',
      'A proportion sets two ratios equal: a/b = c/d. Cross-multiply to solve.'
    ]
  },
  exponent: {
    intro: 'Compute powers, roots and exponent laws.',
    about: [
      'Rules: aᵐ · aⁿ = aᵐ⁺ⁿ. (aᵐ)ⁿ = aᵐⁿ. a⁻ⁿ = 1/aⁿ. a⁰ = 1 for a ≠ 0.',
      'Any positive number can be expressed as a base 10 power — that\'s scientific notation.'
    ]
  },
  rounding: {
    intro: 'Round to nearest, up (ceiling) or down (floor) at any decimal place.',
    about: [
      'Half-up rounds 0.5 up; half-even ("banker\'s rounding") rounds to the nearest even digit and avoids bias over many rounds.',
      'Financial apps commonly use banker\'s rounding to comply with IEEE-754.'
    ]
  },
  'random-number': {
    intro: 'Generate one or many random integers or decimals within any range.',
    about: [
      'The generator uses Math.random() — pseudo-random and NOT cryptographically secure. Adequate for games and demos, not gambling or crypto keys.',
      'For unbiased dice, ensure your generator has range at least 100× the number of possible outcomes.'
    ]
  },
  'big-number': {
    intro: 'Perform arithmetic on numbers of arbitrary size.',
    about: [
      'JavaScript\'s Number type is 64-bit double, precise only to 2^53. Larger integers use BigInt (arbitrary precision).',
      'Big-number arithmetic underpins RSA and other cryptographic algorithms.'
    ]
  },
  'long-division': {
    intro: 'Show every step of a long-division problem.',
    about: [
      'Long division breaks division into repeated multiplication and subtraction at each place.',
      'The remainder can be left as-is, expressed as a fraction, or continued to a decimal expansion.'
    ]
  },
  'common-factor': {
    intro: 'Find the greatest common factor / divisor of two or more integers.',
    about: [
      'Repeatedly apply Euclid\'s algorithm: gcd(a, b) = gcd(b, a mod b) until b = 0.',
      'Prime-factor method: multiply the shared prime factors with the lowest exponents.'
    ]
  },
  'fat-intake': {
    intro: 'Estimate daily fat intake targets based on total calories and macro split.',
    about: [
      'US dietary guidelines suggest 20-35% of calories from fat. Saturated fat should stay under 10% of total calories.',
      'Focus on unsaturated fats (olive oil, nuts, fish) and minimize trans fats.'
    ]
  },
  protein: {
    intro: 'Estimate daily protein needs based on weight and activity level.',
    about: [
      'Sedentary adults: 0.8 g/kg body weight. Endurance athletes: 1.2-1.4 g/kg. Strength athletes: 1.6-2.2 g/kg.',
      'Spread protein across 3-5 meals of 20-40g each for optimal muscle protein synthesis.'
    ]
  },
  carbohydrate: {
    intro: 'Estimate carb intake for weight loss, endurance or general nutrition.',
    about: [
      'Recommended: 45-65% of calories from carbs. Endurance athletes may reach 60-70%; keto diets stay under 5%.',
      'Focus on complex carbs (whole grains, legumes, vegetables) over refined sugars for stable blood glucose.'
    ]
  },
  macro: {
    intro: 'Calculate macro-nutrient targets (protein/carbs/fat) for your calorie goal.',
    about: [
      'Common splits: balanced 40/30/30, high-protein 30/40/30, ketogenic 5/25/70 (carbs/protein/fat).',
      'Each gram of protein and carb = 4 kcal; fat = 9 kcal; alcohol = 7 kcal.'
    ]
  },
  'calories-burned': {
    intro: 'Estimate calories burned per activity based on MET values.',
    about: [
      'MET (metabolic equivalent) = ratio of activity metabolic rate to resting rate. Walking briskly ≈ 3.5 MET, running 6 mph ≈ 10 MET.',
      'Calories/hour ≈ MET × body weight (kg). A 70 kg person running at 10 MET burns ≈ 700 kcal/hr.'
    ]
  },
  tdee: {
    intro: 'Compute Total Daily Energy Expenditure — the calories you burn per day.',
    about: [
      'TDEE = BMR × activity factor. Sedentary 1.2, light 1.375, moderate 1.55, active 1.725, very active 1.9.',
      'For weight loss, eat 15-20% below TDEE. For muscle gain, 10-15% above.'
    ]
  },
  'one-rep-max': {
    intro: 'Estimate your 1-rep max from a submaximal lift using Epley, Brzycki or Lombardi formulas.',
    about: [
      'Epley: 1RM = weight × (1 + reps/30). Reasonably accurate for 2-10 reps.',
      'Never test true 1RM without a spotter. Program between 60-85% of 1RM for most training goals.'
    ]
  },
  'lean-body-mass': {
    intro: 'Estimate lean body mass (LBM) using Boer, James or Hume formulas.',
    about: [
      'LBM = total weight − fat mass. Muscle, bone, organs and water contribute.',
      'LBM is a better metric than body weight for tracking muscle gain during a bulk or preserving muscle during a cut.'
    ]
  },
  'body-surface-area': {
    intro: 'Compute body surface area (BSA) for medical dosing calculations.',
    about: [
      'Common formulas: Du Bois, Mosteller, Haycock. BSA is used to calculate chemo drug doses, cardiac index, and burn assessment.',
      'Average adult BSA is ~1.7 m² (women) to ~2.0 m² (men).'
    ]
  },
  'target-heart-rate': {
    intro: 'Compute training heart-rate zones from age and resting heart rate.',
    about: [
      'Max HR ≈ 220 − age. Karvonen method: THR = ((Max − Rest) × %intensity) + Rest.',
      'Zone 2 (60-70% HRR) burns fat; Zone 4 (80-90%) builds VO₂ max and speed.'
    ]
  },
  'healthy-weight': {
    intro: 'Compute the healthy weight range for your height based on BMI 18.5-24.9.',
    about: [
      'Healthy weight = 18.5-24.9 × height(m)². For 1.75 m: 56.7-76.3 kg.',
      'These ranges are population averages — muscular or athletic builds may weigh above the range without excess fat.'
    ]
  },
  'estimated-energy': {
    intro: 'Estimate Estimated Energy Requirement (EER) using the Institute of Medicine formulas.',
    about: [
      'EER factors in age, sex, weight, height and Physical Activity Level (PAL).',
      'PAL ranges 1.0 (bed-rest) to 2.4 (elite athlete). Most adults fall between 1.4 and 1.6.'
    ]
  },
  'period': {
    intro: 'Track menstrual cycle length and predict future periods.',
    about: [
      'Typical cycle is 21-35 days with 3-7 days of bleeding. Wide variation is normal, especially in the first year after menarche.',
      'Sudden or severe changes warrant a medical check-up.'
    ]
  },
  sleep: {
    intro: 'Compute optimal bedtime based on desired wake time and sleep-cycle length.',
    about: [
      'Sleep cycles average 90 minutes. Waking at the end of a cycle feels more refreshing than mid-cycle.',
      'Adults need 7-9 hours (5-6 full cycles). Consistent bed/wake times improve sleep quality more than total hours alone.'
    ]
  },
  statistics: {
    intro: 'Compute mean, median, mode, standard deviation, variance, quartiles, skewness and range for any dataset.',
    about: [
      'Descriptive statistics summarise the distribution of a sample; inferential statistics let you draw conclusions about the population.',
      'The five-number summary (min, Q1, median, Q3, max) forms the basis of the box-plot.'
    ]
  },
  'mean-median': {
    intro: 'Compute mean, median, mode and range for a dataset.',
    about: [
      'Mean is the arithmetic average, median the middle value, mode the most frequent, range = max − min.',
      'A right-skewed distribution has mean > median; a left-skewed distribution has mean < median.'
    ]
  },
  currency: {
    intro: 'Convert between world currencies (rates approximate — refresh manually).',
    about: [
      'FX rates fluctuate every second in real markets. This tool uses a fixed snapshot — for large trades or precise valuations, always check a live rate.',
      'Retail currency exchange typically costs 2-5% above interbank rates.'
    ]
  },
  countdown: {
    intro: 'Real-time countdown to any target date and time.',
    about: [
      'Updates every second in the browser. Time-zone handling is based on the user\'s local system clock.',
      'Use for launches, exams, weddings and any deadline you want to see tick down.'
    ]
  },
  password: {
    intro: 'Generate strong random passwords with configurable character sets and exclusions.',
    about: [
      'Password strength grows with length far faster than with character-set diversity. A 16-character all-lowercase password beats an 8-character all-symbol password.',
      'Exclude ambiguous characters (I/l/1, O/0) when passwords will be typed manually. Never reuse passwords across sites.'
    ]
  },
  'roman-numeral-date': {
    intro: 'Convert any date to a Roman-numeral rendering used in monuments, movies and copyright lines.',
    about: [
      'Each part of the date (day, month, year) is converted independently using standard Roman numeral rules.',
      'Modern usage often abbreviates the year (e.g. MMXXV = 2025) and joins parts with a dot, slash or space.'
    ]
  },
  'weight-converter': {
    intro: 'Fast conversion between kilograms, pounds, ounces, stones, tonnes and more.',
    about: [
      'Weight and mass are often used interchangeably in daily life but scientifically differ — weight depends on local gravity while mass does not.',
      'On the Moon, your weight is ~1/6 Earth weight but your mass is unchanged.'
    ]
  },
  'mass': {
    intro: 'Convert between kilograms, pounds, ounces, stones, tonnes, atomic mass units and more.',
    about: [
      'Mass is a fundamental physical property, measured in kilograms in SI. It never changes with location; weight = mass × gravity.',
      'Atomic mass unit (u) is 1/12 the mass of a carbon-12 atom — used in chemistry to weigh molecules.'
    ]
  },
  'roman-numeral': {
    intro: 'Convert decimal ↔ Roman numerals — with vinculum notation supporting values up to 3,999,999.',
    about: [
      'Seven symbols: I(1), V(5), X(10), L(50), C(100), D(500), M(1000). Smaller before larger subtracts (IV=4, IX=9, XL=40, CD=400).',
      '[N] in vinculum notation multiplies N by 1000 — e.g. [X] = 10,000, [C]M = 100,000 + 900 = 100,900.'
    ]
  },
  'commission': {
    intro: 'Compute sales commissions — flat rate or tiered.',
    about: [
      'Straight commission: sales × rate. Tiered: different rates over increasing tiers of sales.',
      'On-target earnings (OTE) = base + expected commission at 100% quota. Accelerators pay more above quota.'
    ]
  },
  'square-footage': {
    intro: 'Compute area of nine common shapes with labelled diagrams.',
    about: [
      'Break irregular areas into rectangles and triangles, compute each and sum. Heron\'s formula finds any triangle\'s area from three edge lengths.',
      'Sector area = ½ · r² · θ where θ is in radians. Multiply degrees by π/180 to get radians.'
    ]
  },
  'ip-subnet': {
    intro: 'Compute the network, broadcast, subnet mask, wildcard, usable range and host count for IPv4 and IPv6 subnets.',
    about: [
      'CIDR notation writes a subnet as address / prefix-length. Every /n subnet has 2^(32-n) IPv4 addresses; 2 are reserved so usable hosts = 2^(32-n) − 2.',
      'IPv6 subnets are enormous (128-bit). /64 is the standard end-network prefix.'
    ]
  },
  'url-encode-decode': {
    intro: 'Percent-encode or decode URL query strings and special characters.',
    about: [
      'URLs must limit themselves to ASCII characters. Spaces become %20, and reserved characters (?, &, =, /, #) become %3F, %26, %3D, %2F, %23 when included in query values.',
      'JavaScript\'s <code>encodeURIComponent()</code> handles the encoding; <code>decodeURIComponent()</code> reverses it.'
    ]
  }
};

// Utility so consumers can peek if a description exists
export function hasDescription(slug) {
  return !!DESCRIPTIONS[slug];
}
